import { useEffect, useRef, useState } from 'react';

// Dynamic imports
let faceapi: any = null;
let tf: any = null;

interface FaceIDScannerProps {
  isScanning: boolean;
  onFaceDetected: (descriptor: Float32Array) =>void;
  onError: (error: string) => void;
}

const FaceIDScanner: React.FC<FaceIDScannerProps> = ({
  isScanning,
  onFaceDetected,
  onError
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [isLoadingModels, setIsLoadingModels] = useState(true);
  const streamRef = useRef<MediaStream | null>(null);
  const isProcessingRef = useRef(false);
  const detectionIntervalRef = useRef<number | null>(null);

  // Load TensorFlow.js and face-api.js models
  useEffect(() => {
    const loadModels = async () => {
      try {
        setIsLoadingModels(true);
        
        // Initialize TensorFlow.js backend first
        if (!tf) {
          tf = await import('@tensorflow/tfjs');
          
          try {
            await tf.setBackend('webgl');
            await tf.ready();
          } catch (e) {
            console.warn('WebGL failed, using CPU');
            await tf.setBackend('cpu');
            await tf.ready();
          }
        }
        
        // Load face-api.js
        if (!faceapi) {
          faceapi = await import('@vladmandic/face-api');
        }
        
        // Try loading from local /models first, then fallback to CDN
        try {
          await Promise.all([
            faceapi.nets.tinyFaceDetector.loadFromUri('/models'),
            faceapi.nets.faceLandmark68Net.loadFromUri('/models'),
            faceapi.nets.faceRecognitionNet.loadFromUri('/models')
          ]);
        } catch (localError) {
          console.warn('Local models not found, trying CDN...', localError);
          // Fallback to jsDelivr CDN
          const MODEL_URL = 'https://cdn.jsdelivr.net/gh/justadudewhohacks/face-api.js@0.22.2/weights';
          await Promise.all([
            faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
            faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
            faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL)
          ]);
        }
        
        setModelsLoaded(true);
      } catch (err) {
        console.error('Error loading face-api models:', err);
        onError('Không thể tải models nhận diện khuôn mặt. Vui lòng kiểm tra kết nối internet.');
      } finally {
        setIsLoadingModels(false);
      }
    };
    
    loadModels();
  }, [onError]);

  // Start/stop camera based on isScanning prop
  useEffect(() => {
    if (!isScanning || !modelsLoaded) {
      stopCamera();
      return;
    }

    startCamera();

    return () => {
      stopCamera();
    };
  }, [isScanning, modelsLoaded]);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          width: { ideal: 640 }, 
          height: { ideal: 480 },
          facingMode: 'user'
        }
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        
        // Wait for video to be ready
        await new Promise((resolve) => {
          if (videoRef.current) {
            videoRef.current.onloadedmetadata = resolve;
          }
        });

        // Start face detection
        startDetection();
      }
    } catch (err: any) {
      console.error('Error accessing camera:', err);
      onError('Không thể truy cập camera. Vui lòng cho phép quyền truy cập camera.');
    }
  };

  const stopCamera = () => {
    // Stop detection interval
    if (detectionIntervalRef.current) {
      clearInterval(detectionIntervalRef.current);
      detectionIntervalRef.current = null;
    }

    // Stop camera stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    isProcessingRef.current = false;
  };

  const detectFace = async () => {
    if (!videoRef.current || !modelsLoaded || isProcessingRef.current || !isScanning || !faceapi) {
      return;
    }

    try {
      isProcessingRef.current = true;

      const detections = await faceapi
        .detectSingleFace(videoRef.current, new faceapi.TinyFaceDetectorOptions())
        .withFaceLandmarks()
        .withFaceDescriptor();

      if (detections && detections.descriptor) {
        // Dừng detection ngay sau khi phát hiện khuôn mặt để tránh gọi nhiều lần
        if (detectionIntervalRef.current) {
          clearInterval(detectionIntervalRef.current);
          detectionIntervalRef.current = null;
        }
        // Call callback with descriptor (async, không cần đợi)
        onFaceDetected(detections.descriptor);
        
        // Không reset isProcessingRef để ngăn detection tiếp tục
        // Parent component sẽ control khi nào resume
      } else {
        // Không phát hiện khuôn mặt, tiếp tục scan
        isProcessingRef.current = false;
      }
    } catch (err) {
      console.error('Face detection error:', err);
      isProcessingRef.current = false;
    }
  };

  const startDetection = () => {
    // Clear any existing interval
    if (detectionIntervalRef.current) {
      clearInterval(detectionIntervalRef.current);
    }

    // Run detection every 500ms
    detectionIntervalRef.current = window.setInterval(() => {
      if (isScanning && modelsLoaded) {
        detectFace();
      }
    }, 500);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  if (isLoadingModels) {
    return (
      <div className="flex flex-col items-center justify-center p-8 bg-gray-50 rounded-lg border border-gray-200">
        <div className="w-8 h-8 border-4 border-gray-200 border-t-[#2A4B7C] rounded-full animate-spin mb-3"></div>
        <p className="text-sm text-gray-600">Đang tải models nhận diện khuôn mặt...</p>
      </div>
    );
  }

  if (!modelsLoaded) {
    return (
      <div className="p-8 bg-red-50 rounded-lg border border-red-200 text-red-700 text-center">
        <i className="ri-error-warning-line text-2xl mb-2"></i>
        <p className="text-sm">Không thể tải models nhận diện khuôn mặt</p>
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden rounded-xl">
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className="w-full max-w-md mx-auto rounded-xl shadow-lg"
        style={{ transform: 'scaleX(-1)' }} // Mirror effect
      />
      {isScanning && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          {/* Scanning frame với animation */}
          <div className="relative">
            {/* Main frame */}
            <div className="w-64 h-80 border-3 border-[#00C897] rounded-xl shadow-lg"></div>
            
            {/* Corner indicators */}
            <div className="absolute -top-1 -left-1 w-8 h-8 border-t-4 border-l-4 border-[#00C897] rounded-tl-lg"></div>
            <div className="absolute -top-1 -right-1 w-8 h-8 border-t-4 border-r-4 border-[#00C897] rounded-tr-lg"></div>
            <div className="absolute -bottom-1 -left-1 w-8 h-8 border-b-4 border-l-4 border-[#00C897] rounded-bl-lg"></div>
            <div className="absolute -bottom-1 -right-1 w-8 h-8 border-b-4 border-r-4 border-[#00C897] rounded-br-lg"></div>
            
            {/* Scanning line animation */}
            <div className="absolute inset-0 overflow-hidden rounded-xl">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#00C897] to-transparent animate-scan"></div>
            </div>
          </div>
          
          {/* Overlay với gradient */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent pointer-events-none"></div>
        </div>
      )}
      
      {/* Status indicator */}
      {isScanning && (
        <div className="absolute top-3 right-3 flex items-center gap-2 bg-black/60 backdrop-blur-sm px-3 py-1.5 rounded-full text-white text-xs">
          <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
          <span>Đang quét...</span>
        </div>
      )}
    </div>
  );
};

export default FaceIDScanner;



